import { motion } from "framer-motion";
import { SectionHeading } from "./Reveal";
import { IMAGES } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";
import lowGiImg from "@/assets/low_gi_rice.jpg";
import riceRavvaImg from "@/assets/rice_ravva.jpg";
import bangaruImg from "@/assets/bangaru_theegalu.jpg";
import ayodhyaImg from "@/assets/ayodhya_hmt.jpg";
import idlyRavvaImg from "@/assets/idly_ravva.jpg";
import vasaviSortexImg from "@/assets/vasavi_sortex_superior.jpg";
import vasaviGreenSortexImg from "@/assets/vasavi_green_sortex.jpg";
import vasaviMaroonSortexImg from "@/assets/vasavi_maroon_sortex.jpg";

const Products = () => {
  const { t } = useLanguage();

  const productsList = [
    {
      name: t("products.lowGiName"),
      tag: t("products.lowGiTag"),
      desc: t("products.lowGiDesc"),
      img: lowGiImg,
      badge: t("products.lowGiBadge"),
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.superiorSortexName"),
      tag: t("products.superiorSortexTag"),
      desc: t("products.superiorSortexDesc"),
      img: vasaviSortexImg,
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.greenSortexName"),
      tag: t("products.greenSortexTag"),
      desc: t("products.greenSortexDesc"),
      img: vasaviGreenSortexImg,
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.maroonSortexName"),
      tag: t("products.maroonSortexTag"),
      desc: t("products.maroonSortexDesc"),
      img: vasaviMaroonSortexImg,
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.riceRavvaName"),
      tag: t("products.riceRavvaTag"),
      desc: t("products.riceRavvaDesc"),
      img: riceRavvaImg,
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.bangaruName"),
      tag: t("products.bangaruTag"),
      desc: t("products.bangaruDesc"),
      img: bangaruImg,
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.ayodhyaName"),
      tag: t("products.ayodhyaTag"),
      desc: t("products.ayodhyaDesc"),
      img: ayodhyaImg,
      featured: true,
      fit: "contain",
    },
    {
      name: t("products.idlyRavvaName"),
      tag: t("products.idlyRavvaTag"),
      desc: t("products.idlyRavvaDesc"),
      img: idlyRavvaImg,
      featured: true,
      fit: "contain",
    },
    { name: t("products.sonaName"), tag: t("products.sonaTag"), desc: t("products.sonaDesc"), img: IMAGES.grainsLong },
    { name: t("products.steamName"), tag: t("products.steamTag"), desc: t("products.steamDesc"), img: IMAGES.bowlCooked },
    { name: t("products.rawName"), tag: t("products.rawTag"), desc: t("products.rawDesc"), img: IMAGES.grainsRaw },
    { name: t("products.premName"), tag: t("products.premTag"), desc: t("products.premDesc"), img: IMAGES.grainsMacro },
    { name: t("products.idliName"), tag: t("products.idliTag"), desc: t("products.idliDesc"), img: IMAGES.paddy },
    { name: t("products.brokenName"), tag: t("products.brokenTag"), desc: t("products.brokenDesc"), img: IMAGES.grainsRaw },
  ];

  return (
    <section id="products" className="relative py-24 md:py-36 bg-white overflow-hidden" data-testid="products-section">
      {/* soft ambient blobs */}
      <div className="absolute top-40 -left-24 h-72 w-72 rounded-full bg-[#d8f3dc]/50 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-24 h-72 w-72 rounded-full bg-[#95d5b2]/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionHeading
            eyebrow={t("products.eyebrow")}
            title={`${t("products.title")} ${t("products.titleHighlight")}`}
            subtitle={t("products.subtitle")}
            testid="products"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          {productsList.map((p, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative rounded-[26px] bg-[#f8fff8] border overflow-hidden hover:shadow-2xl transition-all duration-500 flex flex-col justify-between ${
                p.featured
                  ? "border-[#40916c]/60 shadow-lg shadow-[#40916c]/8 hover:shadow-[#40916c]/20"
                  : "border-[#95d5b2]/40 hover:shadow-[#2d6a4f]/12"
              }`}
              data-testid={`product-card-${i}`}
            >
              {/* Image — spotlight clipped frame */}
              <div>
                <div className={`relative h-80 overflow-hidden flex items-center justify-center ${p.featured ? "bg-white p-2" : ""}`}>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2d6a4f]/25 via-transparent to-transparent z-10 opacity-70 group-hover:opacity-90 transition-opacity pointer-events-none" />
                  {/* spotlight glow */}
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 h-40 w-40 rounded-full bg-white/40 blur-3xl z-10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <img
                    src={p.img}
                    alt={`Gupta Enterprises ${p.name}`}
                    loading="lazy"
                    className={`h-full w-full ${p.fit === "contain" ? "object-contain p-2 group-hover:scale-105" : "object-cover scale-105 group-hover:scale-110"} transition-transform duration-700 ease-out`}
                  />
                  <span className={`absolute top-4 left-4 z-20 inline-flex items-center rounded-full px-3.5 py-1 font-body text-xs font-semibold tracking-wide shadow-sm border ${
                    p.featured
                      ? "bg-[#2d6a4f] text-white border-[#2d6a4f]"
                      : "bg-white/85 backdrop-blur text-[#2d6a4f] border-white/60"
                  }`}>
                    {p.tag}
                  </span>
                  <div className="absolute bottom-4 left-5 z-20">
                    <span className="font-body text-[10px] tracking-[0.3em] uppercase text-white/90 drop-shadow">Gupta Enterprises</span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-7">
                  <h3 className="font-display text-2xl font-semibold text-[#222222] flex items-center gap-2">
                    {p.name}
                  </h3>
                  <p className="font-body text-[15px] text-[#5b6b62] leading-relaxed mt-3">{p.desc}</p>
                </div>
              </div>

              <div className="p-7 pt-0">
                <div className="h-px w-full bg-[#40916c]/10 mb-4" />
                <a
                  href="#contact"
                  className={`w-full inline-flex items-center justify-center rounded-full font-body text-xs font-semibold py-3 transition-all ${
                    p.featured
                      ? "bg-[#40916c] text-white hover:bg-[#2d6a4f] shadow-md shadow-[#40916c]/20"
                      : "bg-[#40916c]/10 text-[#2d6a4f] hover:bg-[#40916c] hover:text-white"
                  }`}
                >
                  {t("products.enquireBtn")}
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
